import { DatePipe } from '@angular/common';
import { inject, Injectable, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {filter, merge, Subject, tap, timer} from 'rxjs';
import { DealershipCaseStatus } from 'src/app/shared/enums/dealearship-case-status.enum';
import { SettingsTabType } from 'src/app/shared/enums/settings-tab-type.enum';

import { LocaleService } from '../services/locale.service';
import { SidePanel } from '../../shared/enums/side-panels.enum';
import { environment } from 'src/environments/environment';
import { DEFAULT_ANGULAR_LOCALE } from 'src/app/shared/utils/language.utils';

@Injectable()
export class ScreenStateStore {
  private datePipe = new DatePipe(DEFAULT_ANGULAR_LOCALE);

  private manualRefresh = new Subject<void>();
  readonly manualRefresh$ = this.manualRefresh.asObservable();

  private readonly pollingPaused = signal(false);

  readonly polling$ = timer(environment.pollingInterval, environment.pollingInterval).pipe(
    filter(() => document.visibilityState === 'visible' && !this.pollingPaused()),
    tap(() => this.updateDate()),
  );

  readonly updateAgGridTab$ = merge(
    this.manualRefresh$,
    this.polling$,
  );

  readonly activeAgGridTab = signal(DealershipCaseStatus.OPEN);
  readonly activeSettingsTab = signal(SettingsTabType.WIFI_SETTINGS);

  readonly activePanel = signal('');

  readonly hasFinishedGuidedTour = signal(false);
  readonly lastUpdate = signal('');
  readonly pendingSelectionCaseId = signal<number | undefined>(undefined);

  private readonly translatorService = inject(TranslateService);
  private readonly localeService = inject(LocaleService);

  constructor() {
    this.hasFinishedGuidedTour.set(localStorage.getItem('hasFinishedGuidedTour') === 'true');
  }

  setActiveSettingsTab(tab: SettingsTabType): void {
    this.activeSettingsTab.set(tab);
  }

  openSidePanel(activePanel: SidePanel): void {
    this.activePanel.set(activePanel);
  }

  closeSidePanel(resetSettingsTab?: boolean): void {
    this.activePanel.set('');

    if (resetSettingsTab) {
      this.setActiveSettingsTab(SettingsTabType.WIFI_SETTINGS);
    }
  }

  setActiveAgGridTab(tab: DealershipCaseStatus): void {
    this.activeAgGridTab.set(tab);
    this.manualRefresh.next();
  }

  refreshActiveAgGridTab(): void {
    this.manualRefresh.next();
    this.updateDate();
  }

  setPollingPaused(paused: boolean): void {
    this.pollingPaused.set(paused);
  }

  updateDate(): void {
    this.lastUpdate.set(
      this.translatorService.instant('dashboard.tabs.lastUpdate', {
        date: this.datePipe.transform(new Date(), 'shortTime', undefined, this.localeService.angularLocale()),
      })
    );
  }

  guidedTourFinish(): void {
    this.hasFinishedGuidedTour.set(true);
    localStorage.setItem('hasFinishedGuidedTour', 'true');
  }
}
